import { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@shared/state';
import { doc, getDoc, setDoc, deleteDoc, getFirestore, onSnapshot, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { app, storage } from '@shared/services/firebase';
import { Button } from '../components/ui/Button';
import { NodeContentRenderer } from '../components/learning/NodeContentRenderer';
import type { LearningPath, NodeContent } from '@shared/types/learning-path';
import type { CogniPrintProfile } from '@shared/types/cogniprint';

const db = getFirestore(app);
const functions = getFunctions(app);

const ORIGINAL_PROFILE_KEY = 'cp-content-lab-original-profile';

/**
 * Put the admin's real profile back.
 *
 * The Content Lab overwrites cogniprints/{adminUid} with a preset so the pipeline
 * can be exercised against a known learner shape. The restore used to sit on the
 * success path only, so an exception, a navigation away or a closed tab stranded a
 * synthetic profile (profileVersion 999, questionsAsked 0) on a real account. This
 * is idempotent, safe to call when nothing needs restoring, and — when there is no
 * original to put back — deletes the test profile rather than leaving it standing.
 *
 * See docs/intent/001-diagnostic-redesign/plan.md A6 (defect D-7).
 */
async function restoreOriginalProfile(userId: string): Promise<void> {
  try {
    const profileRef = doc(db, 'cogniprints', userId);
    const snap = await getDoc(profileRef);
    if (!snap.exists()) return;
    const current = snap.data();
    const isTest = current?.isTestProfile === true || current?.profileVersion === 999;
    if (!isTest) return; // the real profile is already in place

    const saved = localStorage.getItem(ORIGINAL_PROFILE_KEY);
    if (saved) {
      await setDoc(profileRef, JSON.parse(saved));
      localStorage.removeItem(ORIGINAL_PROFILE_KEY);
      console.info('[content-lab] original profile restored');
      return;
    }
    // No original to restore — this account had no profile before the lab run.
    // Leaving a synthetic profile behind is worse than having none.
    await deleteDoc(profileRef);
    console.warn('[content-lab] no original profile saved; removed the test profile');
  } catch (e) {
    console.error('[content-lab] could not restore the original profile', e);
  }
}

// ─── Preset types ───

interface PresetConfig {
  label: string;
  category: 'archetype' | 'interaction' | 'edge_case';
  description: string;
  explanation: string;
  expectedBehaviour: string;
  checklist: string[];
  profile: Partial<CogniPrintProfile>;
}

// ─── 23 preset profiles across 3 tiers ───

const PRESETS: Record<string, PresetConfig> = {
  // ═══════════════════════════════════════════════
  // TIER 1: CORE ARCHETYPES (8)
  // ═══════════════════════════════════════════════
  visual_explorer: {
    label: 'Visual Explorer',
    category: 'archetype',
    description: 'Visual-dominant, top-down, essentials depth, low density, curiosity-driven',
    explanation: 'This learner absorbs information primarily through diagrams, concept maps, and visual hierarchies. They prefer to see the big picture first (top-down), want just the key takeaways (essentials), and need breathing room between ideas (low density). Curiosity drives them — they explore because it\'s interesting, not for grades.',
    expectedBehaviour: 'The content window should lead with visual elements: concept maps, flowcharts, colour-coded diagrams. Text should be minimal — 2-4 sentences per concept max. The layout should be concept_map. Nodes should feel spacious, not wall-of-text. Checkpoints should be visual (diagram completion) rather than text-heavy.',
    checklist: [
      'Layout is concept_map',
      'Most nodes have contentFormat "visual" or "mixed"',
      'Visual specs present (flowcharts, concept maps, hierarchies)',
      'Text blocks are short (2-4 sentences per concept)',
      'Depth level is "essentials" on learn nodes',
      'No dense paragraphs — content feels spacious',
      'Checkpoints favour diagram completion or visual matching',
      'Scaffolding uses visual analogies, not long definitions',
      'Overall word count per node is low (<300 words)',
    ],
    profile: {
      contentFormat: { score: { visual: 55, textual: 15, auditory: 5, interactive: 15, video: 10 }, confidence: 0.85 },
      learningApproach: { score: { theoryApplication: 35, reflectiveActive: 30 }, confidence: 0.8 },
      depthTolerance: { score: 20, confidence: 0.8 },
      sequencing: { score: { topDown: 65, bottomUp: 15, modular: 20 }, confidence: 0.8 },
      infoDensity: { score: 25, confidence: 0.75 },
      motivation: { score: { mastery: 20, performance: 15, social: 15, curiosity: 50 }, confidence: 0.7 },
      viewpointRange: { score: 30, confidence: 0.7 },
    },
  },
  systematic_deep_diver: {
    label: 'Systematic Deep-Diver',
    category: 'archetype',
    description: 'Textual-dominant, theory-first, exhaustive depth, high density, mastery-driven',
    explanation: 'This learner wants to understand everything — edge cases, historical context, counterarguments. They prefer structured prose, start with the theory before seeing applications, and can handle dense information. Mastery is the goal: they don\'t move on until they truly understand.',
    expectedBehaviour: 'Dense, thorough textual content. Multiple paragraphs per concept with examples, exceptions, and nuance. Exhaustive depth means nothing is left out. Theory comes before worked examples. High viewpoint range means alternative perspectives should appear. The layout should feel like a well-structured textbook chapter.',
    checklist: [
      'Layout is linear_scroll or concept_map',
      'Most nodes have contentFormat "textual"',
      'Depth level is "exhaustive" or "deep" on learn nodes',
      'Multiple paragraphs per concept (4+)',
      'Historical context or background included',
      'Edge cases and exceptions mentioned',
      'Counterarguments or alternative perspectives present',
      'Worked examples follow theory explanations',
      'Key terms defined with precise definitions',
      'Word count per node is high (500+ words)',
      'Checkpoints are rigorous (case application, mastery check)',
    ],
    profile: {
      contentFormat: { score: { visual: 10, textual: 55, auditory: 5, interactive: 20, video: 10 }, confidence: 0.9 },
      learningApproach: { score: { theoryApplication: 85, reflectiveActive: 70 }, confidence: 0.85 },
      depthTolerance: { score: 90, confidence: 0.9 },
      sequencing: { score: { topDown: 70, bottomUp: 20, modular: 10 }, confidence: 0.85 },
      infoDensity: { score: 85, confidence: 0.8 },
      motivation: { score: { mastery: 55, performance: 20, social: 5, curiosity: 20 }, confidence: 0.8 },
      viewpointRange: { score: 80, confidence: 0.8 },
    },
  },
  hands_on_builder: {
    label: 'Hands-On Builder',
    category: 'archetype',
    description: 'Interactive-dominant, application-first, active, standard depth',
    explanation: 'This learner wants to do, not read. They learn by working through problems, building things, and experimenting. Show them a scenario first, let them try, then reveal the principle behind it. Standard depth — they want enough to be competent, not exhaustive.',
    expectedBehaviour: 'Content should lead with exercises, worked examples, and "your turn" prompts. Theory arrives after the learner has tried something. Interactive elements (fill-in, drag-and-drop, worked examples) should be frequent. Scaffolding is light — they learn by attempting, not by being told.',
    checklist: [
      'Most nodes have contentFormat "interactive" or "mixed"',
      'Interactive elements present (worked_example, fill_in, etc.)',
      'Application/scenario appears before theory explanation',
      'Exercises have clear problem → steps → "your turn" structure',
      'Depth level is "standard" on learn nodes',
      'Text blocks support the exercise, not the other way around',
      'Bottom-up sequencing: builds from specific to general',
      'Checkpoints are problem-solving or case-application type',
      'Language is action-oriented ("try this", "work through")',
    ],
    profile: {
      contentFormat: { score: { visual: 15, textual: 10, auditory: 5, interactive: 55, video: 15 }, confidence: 0.85 },
      learningApproach: { score: { theoryApplication: 15, reflectiveActive: 15 }, confidence: 0.85 },
      depthTolerance: { score: 45, confidence: 0.8 },
      sequencing: { score: { topDown: 15, bottomUp: 55, modular: 30 }, confidence: 0.8 },
      infoDensity: { score: 50, confidence: 0.75 },
      motivation: { score: { mastery: 40, performance: 35, social: 10, curiosity: 15 }, confidence: 0.75 },
      viewpointRange: { score: 40, confidence: 0.7 },
    },
  },
  analytical_reflector: {
    label: 'Analytical Reflector',
    category: 'archetype',
    description: 'Text+visual mix, theory-first, reflective, deep, high viewpoint range',
    explanation: 'This learner thinks deeply. They want to understand the principle, sit with it, consider multiple perspectives, and form their own view. They appreciate both clear prose and supporting diagrams. Reflection prompts help them process. High viewpoint range means they actively want to see counterarguments.',
    expectedBehaviour: 'Content should alternate between structured prose and visual aids. Theory leads. Reflection sections should appear ("Consider this…", "What would happen if…"). Viewpoint toggles with alternative perspectives should be present. Deep depth — thorough without being exhaustive. Modular sequencing lets them explore connections.',
    checklist: [
      'Content mixes "textual" and "visual" formats',
      'Theory/principle appears before examples',
      'Reflection sections present with thought-provoking prompts',
      'Viewpoint toggles or inline counterarguments visible',
      'Depth level is "deep" on learn nodes',
      'Diagrams support and clarify the text, not replace it',
      'Language invites analysis ("consider", "compare", "evaluate")',
      'Multiple angles on the same concept explored',
      'Checkpoints include argument evaluation or open-ended questions',
    ],
    profile: {
      contentFormat: { score: { visual: 30, textual: 40, auditory: 5, interactive: 15, video: 10 }, confidence: 0.85 },
      learningApproach: { score: { theoryApplication: 75, reflectiveActive: 80 }, confidence: 0.85 },
      depthTolerance: { score: 70, confidence: 0.85 },
      sequencing: { score: { topDown: 50, bottomUp: 10, modular: 40 }, confidence: 0.8 },
      infoDensity: { score: 70, confidence: 0.8 },
      motivation: { score: { mastery: 30, performance: 10, social: 10, curiosity: 50 }, confidence: 0.8 },
      viewpointRange: { score: 85, confidence: 0.85 },
    },
  },
  efficient_practitioner: {
    label: 'Efficient Practitioner',
    category: 'archetype',
    description: 'Mixed format, application-first, essentials, high density, performance-driven',
    explanation: 'This learner has limited time and wants maximum efficiency. Give them the key takeaway, a quick example, and move on. High density means pack information tightly — no fluff. Performance-driven means they care about scores, progress bars, and getting things right.',
    expectedBehaviour: 'Content should be dense and efficient. Short paragraphs, no padding. Application examples are brief and practical. Essentials depth — only what\'s needed. Mixed format keeps it engaging without slowing down. Checkpoints should be scored (quiz, scored mastery check) with clear feedback.',
    checklist: [
      'Depth level is "essentials" on learn nodes',
      'Information density feels high — concepts packed tightly',
      'No filler paragraphs or unnecessary context',
      'Examples are practical and brief',
      'Scenario/application comes before theory',
      'Checkpoints are scored (quiz, scored mastery check)',
      'Achievement-oriented language ("master", "score", "complete")',
      'Mixed content formats keep pace varied',
      'Overall feel is efficient — could study in 10-minute blocks',
    ],
    profile: {
      contentFormat: { score: { visual: 20, textual: 25, auditory: 5, interactive: 30, video: 20 }, confidence: 0.8 },
      learningApproach: { score: { theoryApplication: 25, reflectiveActive: 25 }, confidence: 0.8 },
      depthTolerance: { score: 25, confidence: 0.8 },
      sequencing: { score: { topDown: 20, bottomUp: 40, modular: 40 }, confidence: 0.75 },
      infoDensity: { score: 80, confidence: 0.75 },
      motivation: { score: { mastery: 15, performance: 55, social: 10, curiosity: 20 }, confidence: 0.75 },
      viewpointRange: { score: 20, confidence: 0.7 },
    },
  },
  video_learner: {
    label: 'Video-First Learner',
    category: 'archetype',
    description: 'Video+visual dominant, bottom-up, standard depth, curiosity-driven',
    explanation: 'This learner absorbs best through video content and visual aids. They build understanding from concrete examples upward (bottom-up). Standard depth is enough. Curiosity drives exploration — they want to find interesting videos and visual explanations.',
    expectedBehaviour: 'Video nodes should be prominent. Visual specs should accompany text nodes. Video search queries should be generated for most topics. The supporting text should be light — context for the visual/video content, not standalone walls of text.',
    checklist: [
      'Video nodes present in the learning path',
      'Video results populated with relevant YouTube content',
      'Visual specs accompany learn nodes',
      'Text is secondary to video/visual content',
      'Bottom-up sequencing: specific examples before general principles',
      'Depth is "standard" — enough to understand, not exhaustive',
      'Content feels multimedia-first, not text-first',
      'Node types include "video" type explicitly',
    ],
    profile: {
      contentFormat: { score: { visual: 25, textual: 10, auditory: 10, interactive: 10, video: 45 }, confidence: 0.8 },
      learningApproach: { score: { theoryApplication: 30, reflectiveActive: 20 }, confidence: 0.75 },
      depthTolerance: { score: 40, confidence: 0.75 },
      sequencing: { score: { topDown: 20, bottomUp: 50, modular: 30 }, confidence: 0.75 },
      infoDensity: { score: 35, confidence: 0.7 },
      motivation: { score: { mastery: 20, performance: 15, social: 25, curiosity: 40 }, confidence: 0.7 },
      viewpointRange: { score: 50, confidence: 0.7 },
    },
  },
  modular_navigator: {
    label: 'Modular Navigator',
    category: 'archetype',
    description: 'Interactive+visual, modular sequencing, deep, mastery-driven',
    explanation: 'This learner jumps between topics based on interest and connection. They don\'t follow a linear path — they want to explore freely, diving deep into what interests them. Interactive exercises and visual maps of how concepts connect are key. Mastery is the goal.',
    expectedBehaviour: 'Layout should be card_grid (any-order exploration). Concept connections should be highlighted. Interactive elements let them test understanding as they go. Deep depth means thorough coverage within each module. The path should feel like a web, not a line.',
    checklist: [
      'Layout is card_grid or concept_map',
      'Modular sequencing — nodes can be explored in any order',
      'Interactive elements present in most nodes',
      'Visual specs show concept connections and relationships',
      'Depth level is "deep" on learn nodes',
      'Each node is self-contained (doesn\'t assume prior node was read)',
      'Mastery-oriented language ("understand deeply", "build expertise")',
      'Checkpoints test genuine understanding, not recall',
    ],
    profile: {
      contentFormat: { score: { visual: 30, textual: 15, auditory: 5, interactive: 35, video: 15 }, confidence: 0.85 },
      learningApproach: { score: { theoryApplication: 50, reflectiveActive: 45 }, confidence: 0.8 },
      depthTolerance: { score: 65, confidence: 0.8 },
      sequencing: { score: { topDown: 10, bottomUp: 20, modular: 70 }, confidence: 0.85 },
      infoDensity: { score: 55, confidence: 0.75 },
      motivation: { score: { mastery: 50, performance: 20, social: 10, curiosity: 20 }, confidence: 0.8 },
      viewpointRange: { score: 55, confidence: 0.75 },
    },
  },
  auditory_social: {
    label: 'Auditory Social Learner',
    category: 'archetype',
    description: 'Auditory+video dominant, bottom-up, standard, social-driven, low viewpoint',
    explanation: 'This learner processes best through listening and discussion. They\'re motivated by social connection — group context, shared learning, collaborative framing. They build from examples upward. Low viewpoint range means they prefer the consensus view without competing perspectives.',
    expectedBehaviour: 'Since the system can\'t generate audio, it should compensate with video content (spoken explanations), conversational writing tone, and discussion-style framing. Social motivation means progress language should reference community ("learners like you", "commonly asked"). Single viewpoint — no toggles.',
    checklist: [
      'Video nodes prominent (spoken explanations substitute for audio)',
      'Writing tone is conversational, not academic',
      'Social framing present ("many learners find…", "a common question is…")',
      'No viewpoint toggles (low viewpoint range)',
      'Single consensus perspective presented',
      'Bottom-up: examples and stories before principles',
      'Depth is "standard" — approachable, not overwhelming',
      'Content feels like a conversation, not a textbook',
    ],
    profile: {
      contentFormat: { score: { visual: 10, textual: 15, auditory: 35, interactive: 10, video: 30 }, confidence: 0.8 },
      learningApproach: { score: { theoryApplication: 40, reflectiveActive: 35 }, confidence: 0.75 },
      depthTolerance: { score: 45, confidence: 0.75 },
      sequencing: { score: { topDown: 25, bottomUp: 50, modular: 25 }, confidence: 0.75 },
      infoDensity: { score: 40, confidence: 0.7 },
      motivation: { score: { mastery: 15, performance: 15, social: 50, curiosity: 20 }, confidence: 0.75 },
      viewpointRange: { score: 25, confidence: 0.7 },
    },
  },

  // ═══════════════════════════════════════════════
  // TIER 2: DIMENSION INTERACTION TESTS (10)
  // ═══════════════════════════════════════════════
  visual_exhaustive: {
    label: 'Visual + Exhaustive Depth',
    category: 'interaction',
    description: 'Tests: does the system produce detailed, dense diagrams or default to text for depth?',
    explanation: 'This profile creates a tension: the learner wants visual content but also wants exhaustive depth. Diagrams are typically used for simplification — can the system create complex, detailed visual representations that don\'t sacrifice depth? Or does it fall back to text when depth increases?',
    expectedBehaviour: 'Diagrams should be complex and annotated — not simplified overviews. Concept maps with many nodes and labelled edges. Flowcharts with decision branches and edge cases. Supporting text should be brief but the visual specs should carry the depth. If the system just switches to text walls, this test has failed.',
    checklist: [
      'Visual specs are complex (many nodes/edges), not simplified',
      'Diagrams include annotations, labels, and detail',
      'Depth level is "exhaustive" on learn nodes',
      'Content format stays "visual" — does NOT default to "textual"',
      'Edge cases are represented visually, not just in text',
      'Comparison tables or hierarchies show nuanced detail',
      'Supporting text is brief — diagrams do the heavy lifting',
      'Visual complexity matches the exhaustive depth requirement',
    ],
    profile: {
      contentFormat: { score: { visual: 60, textual: 10, auditory: 5, interactive: 15, video: 10 }, confidence: 0.9 },
      learningApproach: { score: { theoryApplication: 50, reflectiveActive: 50 }, confidence: 0.8 },
      depthTolerance: { score: 95, confidence: 0.9 },
      sequencing: { score: { topDown: 50, bottomUp: 25, modular: 25 }, confidence: 0.8 },
      infoDensity: { score: 50, confidence: 0.75 },
      motivation: { score: { mastery: 40, performance: 20, social: 10, curiosity: 30 }, confidence: 0.75 },
      viewpointRange: { score: 50, confidence: 0.75 },
    },
  },
  appfirst_high_viewpoint: {
    label: 'Application-First + High Viewpoint',
    category: 'interaction',
    description: 'Tests: can scenarios include competing perspectives without becoming confusing?',
    explanation: 'This learner wants to start with real scenarios (application-first) but also wants to see multiple perspectives (high viewpoint). The tension: practical scenarios usually have one "right" approach, but high viewpoint demands alternatives. Can the system present a scenario with competing valid approaches?',
    expectedBehaviour: 'Scenarios should present a problem, then show 2-3 valid approaches before revealing the principle. Viewpoint toggles should appear within practical contexts. The content shouldn\'t feel academic — it should feel like "here\'s a real problem with more than one smart answer."',
    checklist: [
      'Scenarios/applications appear before theory',
      'Multiple valid approaches presented for key concepts',
      'Viewpoint toggles or inline counterarguments present',
      'Competing perspectives feel practical, not academic',
      'Content doesn\'t default to one "right answer" on debatable topics',
      'Theory emerges from comparing different approaches',
      'Language is concrete and scenario-based',
      'Viewpoints are integrated into the practical context, not bolted on',
    ],
    profile: {
      contentFormat: { score: { visual: 20, textual: 35, auditory: 5, interactive: 25, video: 15 }, confidence: 0.8 },
      learningApproach: { score: { theoryApplication: 15, reflectiveActive: 40 }, confidence: 0.85 },
      depthTolerance: { score: 55, confidence: 0.8 },
      sequencing: { score: { topDown: 20, bottomUp: 50, modular: 30 }, confidence: 0.8 },
      infoDensity: { score: 50, confidence: 0.75 },
      motivation: { score: { mastery: 30, performance: 20, social: 15, curiosity: 35 }, confidence: 0.75 },
      viewpointRange: { score: 90, confidence: 0.85 },
    },
  },
  interactive_low_density: {
    label: 'Interactive + Low Density',
    category: 'interaction',
    description: 'Tests: are exercises spaced out with breathing room, or crammed together?',
    explanation: 'This learner wants lots of interactive exercises but can only handle a small amount of information at once. The tension: interactive nodes are information-dense by nature (problem + steps + answer). Can the system break exercises into micro-interactions with generous spacing?',
    expectedBehaviour: 'Exercises should be small and focused — one concept per exercise. Lots of white space between sections. No multi-step marathon problems. Each interactive element should feel like a quick, low-pressure check-in. The pace should feel relaxed, not like a workbook.',
    checklist: [
      'Interactive elements are small and focused (1 concept each)',
      'Low info density — generous spacing between sections',
      'No long multi-step problems',
      'Each exercise feels quick (< 2 minutes)',
      'Text between exercises is minimal',
      'Scaffolding is supportive, not overwhelming',
      'Content feels relaxed, not like a test',
      'Nodes have low word count despite being interactive',
    ],
    profile: {
      contentFormat: { score: { visual: 15, textual: 10, auditory: 5, interactive: 55, video: 15 }, confidence: 0.85 },
      learningApproach: { score: { theoryApplication: 30, reflectiveActive: 25 }, confidence: 0.8 },
      depthTolerance: { score: 40, confidence: 0.75 },
      sequencing: { score: { topDown: 30, bottomUp: 40, modular: 30 }, confidence: 0.75 },
      infoDensity: { score: 15, confidence: 0.8 },
      motivation: { score: { mastery: 30, performance: 25, social: 20, curiosity: 25 }, confidence: 0.7 },
      viewpointRange: { score: 35, confidence: 0.7 },
    },
  },
  textual_essentials: {
    label: 'Textual + Essentials Depth',
    category: 'interaction',
    description: 'Tests: does crisp short prose feel substantive or thin?',
    explanation: 'This learner wants text but only the essentials. The tension: textual content usually implies thoroughness. Can the system write crisp, high-quality prose that covers only key insights without feeling incomplete or superficial?',
    expectedBehaviour: 'Each concept should be covered in 2-4 well-crafted sentences. Every word should earn its place. No filler, no over-explanation. Definitions should be precise but brief. The content should feel like expertly edited notes, not a dumbed-down textbook.',
    checklist: [
      'Content format is "textual" on most nodes',
      'Depth is "essentials" — 2-4 sentences per concept',
      'Prose is high-quality: clear, precise, no filler',
      'Key insight is captured without fluff',
      'Definitions are one sentence, not paragraphs',
      'Content doesn\'t feel thin or superficial',
      'Word count per node is low but content feels complete',
      'No padding paragraphs or unnecessary background',
    ],
    profile: {
      contentFormat: { score: { visual: 10, textual: 55, auditory: 5, interactive: 15, video: 15 }, confidence: 0.85 },
      learningApproach: { score: { theoryApplication: 60, reflectiveActive: 50 }, confidence: 0.8 },
      depthTolerance: { score: 18, confidence: 0.85 },
      sequencing: { score: { topDown: 55, bottomUp: 25, modular: 20 }, confidence: 0.8 },
      infoDensity: { score: 50, confidence: 0.75 },
      motivation: { score: { mastery: 25, performance: 30, social: 15, curiosity: 30 }, confidence: 0.75 },
      viewpointRange: { score: 30, confidence: 0.7 },
    },
  },
  theory_modular: {
    label: 'Theory-First + Modular Sequencing',
    category: 'interaction',
    description: 'Tests: can principles be broken into browsable self-contained chunks?',
    explanation: 'This learner wants theory first but also wants to jump between topics freely. The tension: theory-first usually implies a linear progression (concept A underpins concept B). Can the system present principles in modular chunks that each stand alone while still being theory-led?',
    expectedBehaviour: 'Layout should be card_grid. Each card should open with a principle/theory, then provide examples. But each card should be self-contained — no "as we saw in the previous section" dependencies. Cross-references should be links, not prerequisites.',
    checklist: [
      'Layout is card_grid (modular browsing)',
      'Each node opens with the principle/theory',
      'Nodes are self-contained — no dependency on reading order',
      'No "as we discussed earlier" language',
      'Theory leads in each node, followed by examples',
      'Concept connections are referenced but not required',
      'Deep enough to be useful standalone',
      'Card-style presentation works for theory content',
    ],
    profile: {
      contentFormat: { score: { visual: 20, textual: 40, auditory: 5, interactive: 20, video: 15 }, confidence: 0.8 },
      learningApproach: { score: { theoryApplication: 85, reflectiveActive: 55 }, confidence: 0.85 },
      depthTolerance: { score: 55, confidence: 0.8 },
      sequencing: { score: { topDown: 10, bottomUp: 15, modular: 75 }, confidence: 0.85 },
      infoDensity: { score: 55, confidence: 0.75 },
      motivation: { score: { mastery: 45, performance: 15, social: 10, curiosity: 30 }, confidence: 0.75 },
      viewpointRange: { score: 45, confidence: 0.75 },
    },
  },
  video_exhaustive: {
    label: 'Video + Exhaustive Depth',
    category: 'interaction',
    description: 'Tests: does it find long-form deep videos, or give up and switch to text?',
    explanation: 'This learner wants video but also wants exhaustive depth. Most YouTube videos are 10-15 minutes — can the system find in-depth content? Or does it abandon video format when depth is demanded and fall back to text?',
    expectedBehaviour: 'Video search queries should target "in depth", "comprehensive", "full lecture" content. Video nodes should be present. When video alone can\'t deliver exhaustive depth, the supplementary text should complement the video, not replace it.',
    checklist: [
      'Video nodes present in the learning path',
      'Video search queries include depth-seeking terms',
      'Video results are longer-form (not 2-minute explainers)',
      'Content format doesn\'t fully abandon video for text',
      'Supplementary text complements video, not replaces it',
      'Depth is "exhaustive" — even video-heavy nodes cover nuance',
      'Balance maintained between video and supporting text',
      'If video can\'t be found, visual fallbacks provided',
    ],
    profile: {
      contentFormat: { score: { visual: 15, textual: 10, auditory: 10, interactive: 10, video: 55 }, confidence: 0.8 },
      learningApproach: { score: { theoryApplication: 50, reflectiveActive: 40 }, confidence: 0.75 },
      depthTolerance: { score: 90, confidence: 0.85 },
      sequencing: { score: { topDown: 40, bottomUp: 35, modular: 25 }, confidence: 0.75 },
      infoDensity: { score: 60, confidence: 0.7 },
      motivation: { score: { mastery: 35, performance: 15, social: 15, curiosity: 35 }, confidence: 0.7 },
      viewpointRange: { score: 50, confidence: 0.7 },
    },
  },
  high_density_novice: {
    label: 'High Density + Heavy Scaffolding (Novice)',
    category: 'interaction',
    description: 'Tests: can the system pack info densely while still hand-holding a novice?',
    explanation: 'This is a fundamental tension: high density means "pack it tight", but novice scaffolding means "define every term, use analogies, go slowly." Can the system write dense content that still scaffolds every concept? Or does density override scaffolding (or vice versa)?',
    expectedBehaviour: 'Every term should be defined — but briefly. Analogies should be concise, not paragraph-long stories. Information should be packed efficiently despite the definitions. The feel should be "a glossary-equipped reference" rather than either a textbook or a cheat sheet.',
    checklist: [
      'Key terms defined for every new concept',
      'Definitions are brief (one sentence), not paragraph-long',
      'Analogies present but concise',
      'Information density still feels high despite scaffolding',
      'Scaffolding level is "heavy" or "moderate"',
      'Content doesn\'t become sparse due to definitions taking space',
      'Balance achieved: novice can follow, but pace is brisk',
      'No assumed prior knowledge, but no hand-holding fluff either',
    ],
    profile: {
      contentFormat: { score: { visual: 20, textual: 35, auditory: 5, interactive: 25, video: 15 }, confidence: 0.8 },
      learningApproach: { score: { theoryApplication: 50, reflectiveActive: 40 }, confidence: 0.75 },
      depthTolerance: { score: 50, confidence: 0.75 },
      sequencing: { score: { topDown: 45, bottomUp: 30, modular: 25 }, confidence: 0.75 },
      infoDensity: { score: 90, confidence: 0.85 },
      motivation: { score: { mastery: 30, performance: 25, social: 20, curiosity: 25 }, confidence: 0.7 },
      viewpointRange: { score: 35, confidence: 0.7 },
    },
  },
  reflective_performance: {
    label: 'Reflective + Performance-Motivated',
    category: 'interaction',
    description: 'Tests: does reflection coexist with achievement framing, or do they clash?',
    explanation: 'Reflective learners want to pause and think. Performance-motivated learners want scores, progress, and achievement. The tension: reflection is inherently unscored and open-ended, while performance motivation demands measurable outcomes. Can the system frame reflective activities as achievements?',
    expectedBehaviour: 'Reflection prompts should feel purposeful, not idle. They should be framed as "sharpen your thinking" rather than "just sit and wonder." Checkpoints should still be scored where possible. Progress indicators should acknowledge reflection as completed milestones.',
    checklist: [
      'Reflection sections present (node type or section type)',
      'Reflections framed with purpose ("sharpen", "strengthen", "clarify")',
      'Achievement language used ("complete", "demonstrate", "master")',
      'Scored checkpoints still present alongside reflections',
      'Reflective activities feel productive, not idle',
      'Theory-first approach (reflective learning approach)',
      'Progress feels measurable despite reflective content',
      'No tension between "pause to think" and "keep achieving"',
    ],
    profile: {
      contentFormat: { score: { visual: 15, textual: 40, auditory: 5, interactive: 25, video: 15 }, confidence: 0.8 },
      learningApproach: { score: { theoryApplication: 60, reflectiveActive: 85 }, confidence: 0.85 },
      depthTolerance: { score: 60, confidence: 0.8 },
      sequencing: { score: { topDown: 45, bottomUp: 25, modular: 30 }, confidence: 0.75 },
      infoDensity: { score: 55, confidence: 0.75 },
      motivation: { score: { mastery: 15, performance: 55, social: 10, curiosity: 20 }, confidence: 0.8 },
      viewpointRange: { score: 45, confidence: 0.75 },
    },
  },
  auditory_stress_test: {
    label: 'Auditory-Dominant (Stress Test)',
    category: 'interaction',
    description: 'Tests: what happens when the system can\'t deliver the dominant format?',
    explanation: 'The system cannot generate audio content. This profile\'s dominant format is auditory (45%). This is a deliberate stress test: how gracefully does the system fall back? Does it compensate with video (spoken explanations), conversational text, or just ignore the auditory preference entirely?',
    expectedBehaviour: 'The system should acknowledge auditory preference by: prioritising video content (spoken explanations), using conversational/dialogue-style text, suggesting podcast-like content. It should NOT simply produce standard textual content and ignore the auditory signal.',
    checklist: [
      'System does NOT just default to standard text',
      'Video nodes present as an auditory substitute',
      'Writing tone is conversational/dialogue-style',
      'Video queries target explanation/lecture-style content',
      'Content format adapts gracefully (video + conversational text)',
      'No error or blank content due to unsupported format',
      'The justification acknowledges auditory preference',
      'Fallback strategy is intentional, not accidental',
    ],
    profile: {
      contentFormat: { score: { visual: 10, textual: 15, auditory: 45, interactive: 10, video: 20 }, confidence: 0.8 },
      learningApproach: { score: { theoryApplication: 40, reflectiveActive: 35 }, confidence: 0.75 },
      depthTolerance: { score: 45, confidence: 0.75 },
      sequencing: { score: { topDown: 30, bottomUp: 40, modular: 30 }, confidence: 0.75 },
      infoDensity: { score: 45, confidence: 0.7 },
      motivation: { score: { mastery: 25, performance: 20, social: 30, curiosity: 25 }, confidence: 0.7 },
      viewpointRange: { score: 40, confidence: 0.7 },
    },
  },
  visual_bottomup_curiosity: {
    label: 'Visual + Bottom-Up + Curiosity',
    category: 'interaction',
    description: 'Tests: visual exploration from specific to general, driven by curiosity',
    explanation: 'This learner wants visual content, builds understanding from specifics upward, and is driven by curiosity. The combination should produce an exploratory visual experience — detailed example diagrams first, then bigger-picture concept maps that connect them. Curiosity means the content should feel inviting, not prescriptive.',
    expectedBehaviour: 'Specific visual examples should come first (a detailed flowchart of one process), then broader concept maps that show how it connects. Language should be inviting ("notice how…", "what if…", "explore this…"). No rigid prescriptive sequencing — feel of discovery.',
    checklist: [
      'Visual specs lead most nodes',
      'Specific/detailed visuals appear before big-picture maps',
      'Bottom-up sequencing: concrete → abstract',
      'Curiosity-driven language ("discover", "explore", "notice")',
      'Content feels inviting, not prescriptive',
      'Concept maps emerge after specific examples, not before',
      'Overall experience feels like visual exploration/discovery',
      'Tone is warm and wonder-inducing, not directive',
    ],
    profile: {
      contentFormat: { score: { visual: 50, textual: 15, auditory: 5, interactive: 20, video: 10 }, confidence: 0.85 },
      learningApproach: { score: { theoryApplication: 25, reflectiveActive: 30 }, confidence: 0.8 },
      depthTolerance: { score: 50, confidence: 0.75 },
      sequencing: { score: { topDown: 10, bottomUp: 65, modular: 25 }, confidence: 0.85 },
      infoDensity: { score: 40, confidence: 0.75 },
      motivation: { score: { mastery: 15, performance: 10, social: 15, curiosity: 60 }, confidence: 0.8 },
      viewpointRange: { score: 50, confidence: 0.75 },
    },
  },

  // ═══════════════════════════════════════════════
  // TIER 3: EXTREME EDGE CASES (5)
  // ═══════════════════════════════════════════════
  all_minimum: {
    label: 'All Dimensions at Floor',
    category: 'edge_case',
    description: 'Stress test: every dimension at its minimum value',
    explanation: 'Every dimension is set to its floor: application-first, active, essentials depth, low density, low viewpoint, performance-motivated. This is the "fastest, simplest, most action-oriented" possible profile. Tests whether the system can produce genuinely minimal content that still teaches.',
    expectedBehaviour: 'Extremely brief content. One idea per node. Active exercises over text. Application-first throughout. No viewpoints, no depth, no density. Should still be coherent and educational despite the minimalism. If any node has paragraphs of text, the system is ignoring the profile.',
    checklist: [
      'Content is extremely brief per node',
      'Depth is "essentials" throughout',
      'Application/examples come before any theory',
      'No viewpoint toggles or alternative perspectives',
      'No dense paragraphs anywhere',
      'Active learning format (exercises, examples)',
      'Content still makes sense and teaches something',
      'System doesn\'t produce empty or broken nodes',
      'Word count is consistently very low across all nodes',
    ],
    profile: {
      contentFormat: { score: { visual: 20, textual: 20, auditory: 20, interactive: 20, video: 20 }, confidence: 0.7 },
      learningApproach: { score: { theoryApplication: 5, reflectiveActive: 5 }, confidence: 0.7 },
      depthTolerance: { score: 5, confidence: 0.7 },
      sequencing: { score: { topDown: 10, bottomUp: 60, modular: 30 }, confidence: 0.7 },
      infoDensity: { score: 5, confidence: 0.7 },
      motivation: { score: { mastery: 10, performance: 50, social: 20, curiosity: 20 }, confidence: 0.7 },
      viewpointRange: { score: 5, confidence: 0.7 },
    },
  },
  all_maximum: {
    label: 'All Dimensions at Ceiling',
    category: 'edge_case',
    description: 'Stress test: every dimension at its maximum value',
    explanation: 'Every dimension cranked to maximum: theory-first, reflective, exhaustive depth, high density, high viewpoint range. This is the "most thorough, most thoughtful, most complex" possible profile. Tests whether the system can deliver genuinely exhaustive content without breaking or timing out.',
    expectedBehaviour: 'Very long, very thorough content. Multiple paragraphs with counterarguments, historical context, edge cases, exceptions. Viewpoint toggles everywhere. Reflection prompts between sections. This should feel like reading an encyclopaedia article with editorial commentary. If any node feels superficial, the system is capping depth.',
    checklist: [
      'Content is very thorough per node',
      'Depth is "exhaustive" throughout',
      'Theory/principle leads every node',
      'Viewpoint toggles or counterarguments everywhere',
      'Reflection sections present',
      'Historical context and edge cases included',
      'High density — information-packed paragraphs',
      'Word count is consistently very high (500+ per node)',
      'Content doesn\'t feel capped or artificially shortened',
      'System doesn\'t time out or produce errors',
    ],
    profile: {
      contentFormat: { score: { visual: 20, textual: 20, auditory: 20, interactive: 20, video: 20 }, confidence: 0.7 },
      learningApproach: { score: { theoryApplication: 95, reflectiveActive: 95 }, confidence: 0.7 },
      depthTolerance: { score: 95, confidence: 0.7 },
      sequencing: { score: { topDown: 70, bottomUp: 15, modular: 15 }, confidence: 0.7 },
      infoDensity: { score: 95, confidence: 0.7 },
      motivation: { score: { mastery: 50, performance: 10, social: 10, curiosity: 30 }, confidence: 0.7 },
      viewpointRange: { score: 95, confidence: 0.7 },
    },
  },
  max_visual_neutral: {
    label: 'Max Visual, All Else Neutral',
    category: 'edge_case',
    description: 'Isolate test: visual format at 80%, everything else at midpoint',
    explanation: 'Only one dimension is extreme — visual format at 80%. All other dimensions are at their midpoint (50, standard, medium). This isolates the visual format dimension to test whether it alone drives meaningful content differences vs. a neutral profile.',
    expectedBehaviour: 'Content should be overwhelmingly visual: concept maps, flowcharts, hierarchies, timelines. Everything else should feel "average" — standard depth, medium density, balanced approach. If the content looks identical to a neutral profile, the visual signal isn\'t being respected.',
    checklist: [
      'Visual specs present on nearly every learn node',
      'Content format is "visual" on most nodes',
      'Depth is "standard" (neutral midpoint)',
      'Density feels medium — not sparse, not packed',
      'Theory/application balance is roughly even',
      'The ONLY distinctive feature is visual dominance',
      'Content is clearly different from a neutral/balanced profile',
      'Text plays supporting role to visuals, not the other way around',
    ],
    profile: {
      contentFormat: { score: { visual: 80, textual: 5, auditory: 5, interactive: 5, video: 5 }, confidence: 0.9 },
      learningApproach: { score: { theoryApplication: 50, reflectiveActive: 50 }, confidence: 0.75 },
      depthTolerance: { score: 50, confidence: 0.75 },
      sequencing: { score: { topDown: 34, bottomUp: 33, modular: 33 }, confidence: 0.75 },
      infoDensity: { score: 50, confidence: 0.75 },
      motivation: { score: { mastery: 25, performance: 25, social: 25, curiosity: 25 }, confidence: 0.75 },
      viewpointRange: { score: 50, confidence: 0.75 },
    },
  },
  max_depth_neutral: {
    label: 'Max Depth, All Else Neutral',
    category: 'edge_case',
    description: 'Isolate test: exhaustive depth at 95, everything else at midpoint',
    explanation: 'Only depth tolerance is extreme (95 = exhaustive). All other dimensions are neutral. This isolates depth to test whether it alone produces meaningfully longer, more thorough content vs. a neutral profile.',
    expectedBehaviour: 'Content should be visibly longer and more thorough than a neutral profile. Multiple paragraphs per concept. Examples, exceptions, and edge cases included. But the format, sequencing, and style should feel balanced/neutral. The ONLY difference should be thoroughness.',
    checklist: [
      'Depth is "exhaustive" on all learn nodes',
      'Content is significantly longer than other presets',
      'Multiple paragraphs per concept (4+)',
      'Edge cases and exceptions mentioned',
      'Examples are thorough and detailed',
      'Format is balanced (not skewed visual or textual)',
      'Sequencing feels neutral (not strongly top-down or bottom-up)',
      'The ONLY distinctive feature is depth/thoroughness',
    ],
    profile: {
      contentFormat: { score: { visual: 20, textual: 20, auditory: 20, interactive: 20, video: 20 }, confidence: 0.75 },
      learningApproach: { score: { theoryApplication: 50, reflectiveActive: 50 }, confidence: 0.75 },
      depthTolerance: { score: 95, confidence: 0.9 },
      sequencing: { score: { topDown: 34, bottomUp: 33, modular: 33 }, confidence: 0.75 },
      infoDensity: { score: 50, confidence: 0.75 },
      motivation: { score: { mastery: 25, performance: 25, social: 25, curiosity: 25 }, confidence: 0.75 },
      viewpointRange: { score: 50, confidence: 0.75 },
    },
  },
  max_interactive_neutral: {
    label: 'Max Interactive, All Else Neutral',
    category: 'edge_case',
    description: 'Isolate test: interactive format at 80%, everything else at midpoint',
    explanation: 'Only interactive format is extreme (80%). All other dimensions are neutral. This isolates interactivity to test whether the system produces meaningfully more exercises, worked examples, and hands-on elements vs. a neutral profile.',
    expectedBehaviour: 'Nodes should be dominated by interactive elements: worked examples, fill-in, exercises, practice problems. Text should support the exercises, not the other way around. Everything else (depth, density, approach) should feel average.',
    checklist: [
      'Interactive elements present on nearly every learn node',
      'Content format is "interactive" on most nodes',
      'Worked examples and exercises are the primary content',
      'Text supports exercises, not the other way around',
      'Depth is "standard" (neutral midpoint)',
      'Density and approach feel balanced/neutral',
      'The ONLY distinctive feature is interactive dominance',
      'Content is clearly different from a neutral/balanced profile',
    ],
    profile: {
      contentFormat: { score: { visual: 5, textual: 5, auditory: 5, interactive: 80, video: 5 }, confidence: 0.9 },
      learningApproach: { score: { theoryApplication: 50, reflectiveActive: 50 }, confidence: 0.75 },
      depthTolerance: { score: 50, confidence: 0.75 },
      sequencing: { score: { topDown: 34, bottomUp: 33, modular: 33 }, confidence: 0.75 },
      infoDensity: { score: 50, confidence: 0.75 },
      motivation: { score: { mastery: 25, performance: 25, social: 25, curiosity: 25 }, confidence: 0.75 },
      viewpointRange: { score: 50, confidence: 0.75 },
    },
  },
};

type Stage =
  | 'idle'
  | 'uploading'
  | 'processing'
  | 'generating_path'
  | 'generating_content'
  | 'complete'
  | 'error';

export function AdminContentLab() {
  const user = useAuthStore((s) => s.user);
  const [roleChecked, setRoleChecked] = useState(false);
  // isAdmin kept for internal role check; redirect removed since AdminRoute handles it.
  const [, setIsAdmin] = useState(false);

  // Upload state
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Profile preset
  const [selectedPreset, setSelectedPreset] = useState('visual_explorer');

  // Pipeline state
  const [stage, setStage] = useState<Stage>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Result state
  const [learningPath, setLearningPath] = useState<LearningPath | null>(null);
  const [nodeContents, setNodeContents] = useState<Map<string, NodeContent>>(new Map());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // ─── Checkpoint persistence ───
  const CHECKPOINT_KEY = 'cp-content-lab-checkpoint';

  interface PipelineCheckpoint {
    contentId: string;
    pathId: string | null;
    preset: string;
    stage: Stage;
    savedAt: string;
  }

  function saveCheckpoint(cp: Partial<PipelineCheckpoint> & { contentId: string }) {
    try {
      const existing = loadCheckpoint();
      const merged = { ...existing, ...cp, savedAt: new Date().toISOString() };
      localStorage.setItem(CHECKPOINT_KEY, JSON.stringify(merged));
    } catch { /* ignore */ }
  }

  function loadCheckpoint(): PipelineCheckpoint | null {
    try {
      const raw = localStorage.getItem(CHECKPOINT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  function clearCheckpoint() {
    try { localStorage.removeItem(CHECKPOINT_KEY); } catch { /* ignore */ }
  }

  const [hasResumable, setHasResumable] = useState(false);
  const [resumeInfo, setResumeInfo] = useState<PipelineCheckpoint | null>(null);

  // A stranded test profile survives a closed tab, so sweep for one on mount.
  useEffect(() => {
    if (!user) return;
    void restoreOriginalProfile(user.uid);
  }, [user]);

  useEffect(() => {
    const cp = loadCheckpoint();
    if (cp && cp.contentId && cp.stage !== 'complete' && cp.stage !== 'idle') {
      setHasResumable(true);
      setResumeInfo(cp);
    }
    // Mount-only: probe localStorage for a resumable checkpoint exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Checklist state — persisted to localStorage
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('cp-content-lab-checklist');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    try { localStorage.setItem('cp-content-lab-checklist', JSON.stringify(checkedItems)); } catch { /* ignore */ }
  }, [checkedItems]);

  // Admin check
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) return;
        setIsAdmin(snap.data()?.role === 'admin');
      } finally {
        if (!cancelled) setRoleChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // ─── Shared pipeline stages (used by both fresh run and resume) ───

  // Statuses that mean the document is parsed and ready for path generation.
  // The `handleUpload` storage trigger writes `concepts` and sets status to
  // `assessing` in the same update, so `assessing` is its terminal success
  // state. `embedding`/`complete`/`ready` are downstream states reached only
  // once the (optional) assessPriorKnowledge + prepareEmbeddings callables run
  // — which the Content Lab does not invoke. generateLearningPath only needs
  // `concepts`, so any of these statuses means we can proceed.
  const PARSED_STATUSES = ['assessing', 'embedding', 'complete', 'ready'];

  async function waitForProcessing(contentId: string): Promise<void> {
    setStage('processing');
    setStatusMessage('Processing document (CE-001)…');
    saveCheckpoint({ contentId, stage: 'processing', preset: selectedPreset, pathId: null });

    // Check if already processed
    const existingSnap = await getDoc(doc(db, 'content', contentId));
    const existingStatus = existingSnap.data()?.processingStatus;
    if (existingStatus && PARSED_STATUSES.includes(existingStatus)) {
      setStatusMessage('Processing already complete — skipping.');
      return;
    }
    if (existingStatus === 'error') {
      throw new Error(existingSnap.data()?.processingError ?? 'Processing failed');
    }

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Processing timed out after 5 minutes')), 300000);
      const unsub = onSnapshot(doc(db, 'content', contentId), (snap) => {
        const data = snap.data();
        if (!data) return;
        setStatusMessage(`Processing: ${data.processingStatus}…`);
        if (PARSED_STATUSES.includes(data.processingStatus)) {
          clearTimeout(timeout);
          unsub();
          resolve();
        }
        if (data.processingStatus === 'error') {
          clearTimeout(timeout);
          unsub();
          reject(new Error(data.processingError ?? 'Processing failed'));
        }
      });
    });
  }

  async function generatePath(contentId: string): Promise<string> {
    setStage('generating_path');
    setStatusMessage('Generating learning path (LA-001, Opus)…');
    saveCheckpoint({ contentId, stage: 'generating_path', preset: selectedPreset, pathId: null });

    // The Firebase callable SDK defaults to a 70s client timeout, but these
    // functions are configured for up to 300s server-side. Raise the client
    // timeout to 9 min so a slow Opus/Sonnet generation isn't aborted with
    // `deadline-exceeded` while the function is still running.
    const genPathFn = httpsCallable<{ contentId: string }, { pathId: string; nodeCount: number }>(
      functions, 'generateLearningPath', { timeout: 540000 },
    );
    const pathResult = await genPathFn({ contentId });
    const pathId = pathResult.data.pathId;
    setStatusMessage(`Path created: ${pathResult.data.nodeCount} nodes. Generating content…`);
    saveCheckpoint({ contentId, stage: 'generating_content', preset: selectedPreset, pathId });
    return pathId;
  }

  async function generateContent(pathId: string, contentId: string): Promise<void> {
    setStage('generating_content');
    setStatusMessage('Generating node content (LA-002, Sonnet)…');
    saveCheckpoint({ contentId, stage: 'generating_content', preset: selectedPreset, pathId });

    const pathUnsub = onSnapshot(doc(db, 'learningPaths', pathId), (snap) => {
      const data = snap.data();
      if (!data) return;
      const progress = data.generationProgress as { totalNodes: number; completedNodes: number } | null;
      if (progress) {
        setStatusMessage(`Generating content: ${progress.completedNodes}/${progress.totalNodes} nodes…`);
      }
    });

    // See generatePath: override the 70s default so multi-node Sonnet
    // generation (batches of 5, up to 300s server-side) isn't aborted early.
    const genContentFn = httpsCallable(functions, 'generateNodeContent', { timeout: 540000 });
    await genContentFn({ pathId });
    pathUnsub();
  }

  async function loadResults(pathId: string, presetLabel: string): Promise<void> {
    setStatusMessage('Loading results…');
    const pathSnap = await getDoc(doc(db, 'learningPaths', pathId));
    const pathData = pathSnap.data() as LearningPath;
    setLearningPath(pathData);

    const nodesSnap = await getDocs(collection(db, 'learningPaths', pathId, 'nodes'));
    const contents = new Map<string, NodeContent>();
    nodesSnap.forEach((d) => contents.set(d.id, d.data() as NodeContent));
    setNodeContents(contents);

    if (pathData.nodes.length > 0) {
      setSelectedNodeId(pathData.nodes[0].nodeId);
    }

    setStage('complete');
    setStatusMessage(`Done. ${pathData.nodes.length} nodes generated for "${presetLabel}" profile.`);
    clearCheckpoint();
  }

  // ─── Fresh pipeline run ───

  const runPipeline = useCallback(async () => {
    if (!user || !file) return;

    setError(null);
    setLearningPath(null);
    setNodeContents(new Map());
    setSelectedNodeId(null);
    setHasResumable(false);

    const userId = user.uid;
    const preset = PRESETS[selectedPreset];

    try {
      // ─── 1. Save original profile & overwrite with preset ───
      setStage('uploading');
      setStatusMessage('Saving test profile…');

      const profileRef = doc(db, 'cogniprints', userId);
      const originalSnap = await getDoc(profileRef);
      const originalProfile = originalSnap.exists() ? originalSnap.data() : null;

      // Save original profile to localStorage for recovery
      if (originalProfile) {
        try { localStorage.setItem(ORIGINAL_PROFILE_KEY, JSON.stringify(originalProfile)); } catch { /* ignore */ }
      }

      const now = new Date().toISOString();
      const testProfile: CogniPrintProfile = {
        profileVersion: 999,
        diagnosticType: 'full',
        userId,
        createdAt: now,
        lastUpdated: now,
        questionsAsked: 0,
        questionsCarriedForward: 0,
        overallConfidence: 0.85,
        archetypeMatch: preset.label,
        adaptiveNotes: `[Content Lab test] ${preset.label}`,
        summary: null,
        // Marks this document as synthetic. Nothing may render it to a learner or
        // treat it as evidence — see isTestProfile() in @shared/utils/profile-validation.
        isTestProfile: true,
        contentFormat: preset.profile.contentFormat!,
        learningApproach: preset.profile.learningApproach!,
        depthTolerance: preset.profile.depthTolerance!,
        sequencing: preset.profile.sequencing!,
        infoDensity: preset.profile.infoDensity!,
        motivation: preset.profile.motivation!,
        viewpointRange: preset.profile.viewpointRange!,
      };

      await setDoc(profileRef, testProfile);

      // ─── 2. Upload file ───
      setStatusMessage('Uploading content…');
      const contentId = `lab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const storagePath = `uploads/${userId}/${contentId}/${file.name}`;
      const storageRef = ref(storage, storagePath);
      saveCheckpoint({ contentId, stage: 'uploading', preset: selectedPreset, pathId: null });

      await new Promise<void>((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, file, { contentType: file.type });
        uploadTask.on(
          'state_changed',
          (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          () => resolve(),
        );
      });

      // ─── 3–6. Processing → Path → Content → Results ───
      await waitForProcessing(contentId);
      const pathId = await generatePath(contentId);
      await generateContent(pathId, contentId);
      await loadResults(pathId, preset.label);

    } catch (err) {
      setStage('error');
      const msg = err instanceof Error ? err.message : 'Pipeline failed';
      setError(msg);
      setStatusMessage('');
    } finally {
      // Restore on EVERY exit — success, failure, or a throw from anywhere above.
      await restoreOriginalProfile(userId);
    }
    // In-scope pipeline helpers (generatePath/generateContent/loadResults/etc.)
    // are stable for this admin tool and invoked on click, not during render;
    // re-keying the callback on them would cascade useCallback across all of them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, file, selectedPreset]);

  // ─── Resume from checkpoint ───

  const resumePipeline = useCallback(async () => {
    if (!user) return;
    const cp = loadCheckpoint();
    if (!cp) return;

    setError(null);
    setLearningPath(null);
    setNodeContents(new Map());
    setSelectedNodeId(null);
    setHasResumable(false);

    const presetKey = cp.preset || selectedPreset;
    const preset = PRESETS[presetKey] ?? PRESETS.visual_explorer;

    try {
      setStatusMessage(`Resuming from "${cp.stage}"…`);

      // Determine where to resume
      if (cp.stage === 'uploading' || cp.stage === 'processing') {
        await waitForProcessing(cp.contentId);
        const pathId = await generatePath(cp.contentId);
        await generateContent(pathId, cp.contentId);
        await loadResults(pathId, preset.label);
      } else if (cp.stage === 'generating_path') {
        const pathId = await generatePath(cp.contentId);
        await generateContent(pathId, cp.contentId);
        await loadResults(pathId, preset.label);
      } else if (cp.stage === 'generating_content' && cp.pathId) {
        await generateContent(cp.pathId, cp.contentId);
        await loadResults(cp.pathId, preset.label);
      }

    } catch (err) {
      setStage('error');
      const msg = err instanceof Error ? err.message : 'Resume failed';
      setError(msg);
      setStatusMessage('');
    } finally {
      await restoreOriginalProfile(user.uid);
    }
    // Same rationale as runPipeline: in-scope helpers are stable and click-driven.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedPreset]);

  // Auth + role guard is handled by AdminRoute wrapper.
  // Keep a loading state while the internal role check completes (needed
  // for the standalone /admin-content-lab route fallback).
  if (!user) return <Navigate to="/login" replace />;
  if (!roleChecked) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const selectedNode = learningPath?.nodes.find((n) => n.nodeId === selectedNodeId) ?? null;
  const selectedContent = selectedNodeId ? nodeContents.get(selectedNodeId) ?? null : null;
  const isRunning = stage !== 'idle' && stage !== 'complete' && stage !== 'error';

  return (
    <div className="max-w-[1600px] mx-auto px-4 py-8">
      <h1 className="font-heading text-2xl font-bold mb-1">Content Lab</h1>
      <p className="text-content-muted text-sm mb-6">
        Upload study material, pick a CogniPrint preset, and see exactly what the system produces.
      </p>

      {/* Resume banner */}
      {hasResumable && resumeInfo && (
        <div
          className="rounded-xl p-4 mb-6 flex items-center justify-between"
          style={{
            background: 'rgba(240,165,0,0.08)',
            border: '1px solid rgba(240,165,0,0.25)',
          }}
        >
          <div>
            <p className="text-sm font-semibold text-gold-400">Previous run can be resumed</p>
            <p className="text-xs text-content-muted">
              Stopped at "{resumeInfo.stage}" · {resumeInfo.contentId.slice(0, 20)}… · {new Date(resumeInfo.savedAt).toLocaleString()}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={resumePipeline}
              className="text-xs font-semibold rounded-lg px-3 py-1.5 text-white"
              style={{ background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)' }}
            >
              Resume
            </button>
            <button
              onClick={() => { clearCheckpoint(); setHasResumable(false); setResumeInfo(null); }}
              className="text-xs text-content-muted hover:text-content px-2 py-1.5"
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {/* ─── Controls ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* 1. Upload */}
        <section className="rounded-2xl bg-dark-surface p-5 border border-dark-border">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-content-muted mb-3">
            1. Upload Content
          </h2>
          <label className="block">
            <div className="flex items-center justify-center w-full h-28 rounded-xl border-2 border-dashed border-dark-border hover:border-indigo-500/50 transition-colors cursor-pointer bg-dark-subtle">
              <input
                type="file"
                accept=".pdf,.docx,.txt"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={isRunning}
              />
              {file ? (
                <div className="text-center px-3">
                  <p className="text-sm font-semibold text-content truncate max-w-[200px]">{file.name}</p>
                  <p className="text-xs text-content-muted">{(file.size / 1024).toFixed(0)} KB</p>
                </div>
              ) : (
                <p className="text-sm text-content-muted">Click to select PDF, DOCX, or TXT</p>
              )}
            </div>
          </label>
          {stage === 'uploading' && uploadProgress > 0 && (
            <div className="mt-2 h-1.5 rounded-full bg-dark-subtle overflow-hidden">
              <div
                className="h-full bg-indigo-500 transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
        </section>

        {/* 2. Profile Preset */}
        <section className="rounded-2xl bg-dark-surface p-5 border border-dark-border">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-content-muted mb-3">
            2. Diagnostic Configuration
          </h2>
          <select
            value={selectedPreset}
            onChange={(e) => setSelectedPreset(e.target.value)}
            disabled={isRunning}
            className="w-full rounded-lg text-sm p-2.5 mb-2 bg-dark-subtle border border-dark-border text-content"
          >
            <optgroup label="Core Archetypes">
              {Object.entries(PRESETS).filter(([, p]) => p.category === 'archetype').map(([key, p]) => (
                <option key={key} value={key}>{p.label}</option>
              ))}
            </optgroup>
            <optgroup label="Dimension Interaction Tests">
              {Object.entries(PRESETS).filter(([, p]) => p.category === 'interaction').map(([key, p]) => (
                <option key={key} value={key}>{p.label}</option>
              ))}
            </optgroup>
            <optgroup label="Extreme Edge Cases">
              {Object.entries(PRESETS).filter(([, p]) => p.category === 'edge_case').map(([key, p]) => (
                <option key={key} value={key}>{p.label}</option>
              ))}
            </optgroup>
          </select>
          <p className="text-xs text-content-muted">{PRESETS[selectedPreset].description}</p>

          {/* Mini profile preview */}
          <div className="mt-3 space-y-1.5">
            {(() => {
              const p = PRESETS[selectedPreset].profile;
              const cf = p.contentFormat?.score;
              const la = p.learningApproach?.score;
              const dt = p.depthTolerance?.score as number;
              const sq = p.sequencing?.score;
              const depthLabel = dt <= 25 ? 'Essentials' : dt <= 50 ? 'Standard' : dt <= 75 ? 'Deep' : 'Exhaustive';
              const dominant = cf ? Object.entries(cf).sort((a, b) => (b[1] as number) - (a[1] as number))[0] : null;
              const seqDominant = sq ? Object.entries(sq).sort((a, b) => (b[1] as number) - (a[1] as number))[0] : null;

              return (
                <>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#6C5CE7]" />
                    <span className="text-xs text-content-secondary">
                      Format: <span className="text-content">{dominant?.[0]} ({dominant?.[1]}%)</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#0ABFBC]" />
                    <span className="text-xs text-content-secondary">
                      Approach: <span className="text-content">
                        {(la?.theoryApplication ?? 50) > 50 ? 'Theory-first' : 'Application-first'} · {(la?.reflectiveActive ?? 50) > 50 ? 'Reflective' : 'Active'}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#F0A500]" />
                    <span className="text-xs text-content-secondary">
                      Depth: <span className="text-content">{depthLabel} ({dt})</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#55EFC4]" />
                    <span className="text-xs text-content-secondary">
                      Sequencing: <span className="text-content">{seqDominant?.[0]} ({seqDominant?.[1]}%)</span>
                    </span>
                  </div>
                </>
              );
            })()}
          </div>
        </section>

        {/* 3. Generate */}
        <section className="rounded-2xl bg-dark-surface p-5 border border-dark-border flex flex-col">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-content-muted mb-3">
            3. Generate
          </h2>
          <Button
            onClick={runPipeline}
            disabled={!file || isRunning}
            className="mb-3"
          >
            {isRunning ? 'Generating…' : 'Personalise My Content'}
          </Button>

          {/* Status */}
          {statusMessage && (
            <div className="flex items-center gap-2 text-sm text-content-secondary">
              {isRunning && (
                <div className="w-3.5 h-3.5 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin shrink-0" />
              )}
              {statusMessage}
            </div>
          )}
          {error && (
            <p className="text-sm text-red-400 mt-2">{error}</p>
          )}

          {/* Stage indicator */}
          {isRunning && (
            <div className="mt-auto pt-4 flex gap-1">
              {(['uploading', 'processing', 'generating_path', 'generating_content'] as Stage[]).map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s === stage ? 'bg-indigo-500 animate-pulse' :
                    (['uploading', 'processing', 'generating_path', 'generating_content'].indexOf(s) <
                     ['uploading', 'processing', 'generating_path', 'generating_content'].indexOf(stage))
                      ? 'bg-indigo-500' : 'bg-dark-subtle'
                  }`}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ─── Results ─── */}
      {learningPath && (
        <>
          {/* Path metadata + node selector (outside the viewing window) */}
          <div className="flex items-center gap-4 flex-wrap mb-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-content-muted">
              Layout: <span className="text-content">{learningPath.layout}</span>
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-content-muted">
              Nodes: <span className="text-content">{learningPath.nodes.length}</span>
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-content-muted">
              Duration: <span className="text-content">~{learningPath.totalEstimatedDuration} min</span>
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-content-muted">
              Profile: <span className="text-indigo-400">{PRESETS[selectedPreset].label}</span>
            </span>
          </div>

          {/* Node selector tabs (outside the viewing window) */}
          <div className="flex gap-1.5 flex-wrap mb-3">
            {learningPath.nodes.map((node, i) => (
              <button
                key={node.nodeId}
                onClick={() => setSelectedNodeId(node.nodeId)}
                className={`px-3 py-2 rounded-lg text-left transition-colors border ${
                  node.nodeId === selectedNodeId
                    ? 'bg-indigo-500/15 border-indigo-500/40 text-content'
                    : 'bg-dark-surface border-dark-border hover:bg-dark-subtle text-content-secondary'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-bold text-content-muted">{i + 1}</span>
                  <span className={`text-[10px] font-bold uppercase px-1 py-0.5 rounded ${
                    node.nodeType === 'learn' ? 'bg-indigo-500/15 text-indigo-400' :
                    node.nodeType === 'checkpoint' ? 'bg-gold-500/15 text-gold-400' :
                    node.nodeType === 'exercise' ? 'bg-emerald-500/15 text-emerald-400' :
                    node.nodeType === 'reflection' ? 'bg-amber-500/15 text-amber-400' :
                    'bg-dark-subtle text-content-muted'
                  }`}>
                    {node.nodeType}
                  </span>
                </div>
                <div className="text-[11px] truncate max-w-[140px] mt-0.5">
                  {nodeContents.get(node.nodeId)?.content?.title ?? `Node ${i + 1}`}
                </div>
                <div className="text-[10px] text-content-dim">
                  {node.contentFormat} · {node.depthLevel} · ~{node.estimatedDuration}m
                </div>
              </button>
            ))}
          </div>

          {/* LA-001 justification (outside the viewing window) */}
          {selectedNode && (
            <div className="mb-3 p-3 rounded-lg bg-dark-subtle border border-dark-border">
              <p className="text-[10px] font-bold uppercase tracking-wider text-content-muted mb-1">
                LA-001 Justification
              </p>
              <p className="text-xs text-content-secondary">{selectedNode.profileJustification}</p>
            </div>
          )}

          {/* ─── Viewing Window: study content only ─── */}
          <div className="rounded-2xl bg-dark-surface border border-dark-border overflow-y-auto min-h-[700px] max-h-[850px] p-8">
            {selectedNode && selectedContent ? (
              <NodeContentRenderer
                nodeContent={selectedContent}
                node={selectedNode}
                pathId={learningPath.pathId}
              />
            ) : selectedNodeId ? (
              <div className="flex items-center justify-center h-[600px] text-content-muted text-sm">
                No content generated for this node yet.
              </div>
            ) : (
              <div className="flex items-center justify-center h-[600px] text-content-muted text-sm">
                Select a node above to preview its content.
              </div>
            )}
          </div>

          {/* ─── Evaluation Panel: below the viewing window ─── */}
          {(() => {
            const preset = PRESETS[selectedPreset];
            const categoryLabel = preset.category === 'archetype' ? 'Core Archetype' : preset.category === 'interaction' ? 'Dimension Interaction Test' : 'Extreme Edge Case';
            const categoryColour = preset.category === 'archetype' ? 'indigo' : preset.category === 'interaction' ? 'gold' : 'red';
            const checkedCount = preset.checklist.filter((_, i) => checkedItems[`${selectedPreset}_${i}`]).length;
            const totalCount = preset.checklist.length;
            const allPassed = checkedCount === totalCount;

            return (
              <div className="mt-6 rounded-2xl bg-dark-surface border border-dark-border overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-dark-border flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-${categoryColour}-500/15 text-${categoryColour}-400`}>
                        {categoryLabel}
                      </span>
                      <h3 className="font-heading font-bold text-lg">{preset.label}</h3>
                    </div>
                    <p className="text-sm text-content-muted">{preset.description}</p>
                  </div>
                  <div className={`text-right ${allPassed ? 'text-emerald-400' : 'text-content-muted'}`}>
                    <div className="font-heading font-bold text-2xl">{checkedCount}/{totalCount}</div>
                    <div className="text-[10px] uppercase tracking-wider">{allPassed ? 'All passed' : 'Checked'}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-dark-border">
                  {/* Left: Explanation */}
                  <div className="p-6 space-y-4">
                    <div>
                      <h4 className="text-[11px] font-bold uppercase tracking-wider text-content-muted mb-2">
                        What this profile means for the learner
                      </h4>
                      <p className="text-sm text-content-secondary leading-relaxed">{preset.explanation}</p>
                    </div>
                    <div>
                      <h4 className="text-[11px] font-bold uppercase tracking-wider text-content-muted mb-2">
                        What it should look like in the window
                      </h4>
                      <p className="text-sm text-content-secondary leading-relaxed">{preset.expectedBehaviour}</p>
                    </div>
                  </div>

                  {/* Right: Checklist */}
                  <div className="p-6">
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-content-muted mb-3">
                      Verification checklist
                    </h4>
                    <div className="space-y-2">
                      {preset.checklist.map((item, i) => {
                        const key = `${selectedPreset}_${i}`;
                        const isChecked = !!checkedItems[key];
                        return (
                          <label
                            key={key}
                            className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              isChecked ? 'bg-emerald-500/8' : 'hover:bg-dark-subtle'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => setCheckedItems((prev) => ({ ...prev, [key]: e.target.checked }))}
                              className="mt-0.5 rounded border-dark-border accent-emerald-500"
                            />
                            <span className={`text-sm ${isChecked ? 'text-emerald-400 line-through opacity-80' : 'text-content-secondary'}`}>
                              {item}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
